import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { createDriver } from '@/modules/driver/actions/async/create-driver';
import { getCity } from '@/modules/partner/store/selectors';
import { AppDispatch } from '@/root-store';
import { useLicensingAuthorityTypes } from '@/shared/hooks/useLicensingAuthorityTypes';
import { CountryCode } from '@/shared/models/country-code';
import { LicensingAuthorityTypes } from '@/shared/models/licensingAuthority';
import { getDefaultPhoneCode } from '@/shared/utils/getDefaultPhoneCode';
import { useForm } from '@/shared/view/Form';
import { StepLayout } from '@/shared/view/StepLayout';

import { ICreateDriverForm } from './types';
import { CreateDriverView } from './view';

const getNewEmptyDriver = ({ phoneCode }: { phoneCode: string }): ICreateDriverForm => ({
  birthday: null,
  email: '',
  firstName: '',
  homeAddressLine1: '',
  homeAddressLine2: '',
  homeAddressCity: '',
  homeAddressState: '',
  homeAddressPostcode: '',
  lastName: '',
  licenseNumber: '',
  licensingAuthority: LicensingAuthorityTypes.TFL,
  personalInn: '',
  phoneCode: phoneCode || '',
  phoneNumber: '',
  ukNationalInsurance: '',
});

interface IProps {
  onNavigateBack: (path: string) => void;
  onNavigate: (path: string, replace?: boolean) => void;
}

const CreateDriver = ({ onNavigateBack, onNavigate }: IProps) => {
  const countryCode = useSelector(getCity)?.countryCode ?? CountryCode.Unknown;
  const defaultPhoneCode = getDefaultPhoneCode(countryCode);

  const dispatch = useDispatch() as AppDispatch;

  const licensingAuthorityTypes = useLicensingAuthorityTypes();

  const postForm = useCallback(
    async (formValues: Parameters<typeof createDriver>[0]['formValues']) => {
      const resultAction = await dispatch(createDriver({ formValues, countryCode }));

      if (createDriver.fulfilled.match(resultAction)) {
        const result = resultAction.payload;

        if (!result.validationErrors && result.driver) {
          onNavigate(`/chauffeurs/${result.driver.id}#docs`, true);
        }

        return result;
      }

      return {};
    },
    [dispatch, onNavigate, countryCode],
  );

  const {
    handleSubmit,
    form: {
      control,
      formState: { isSubmitting },
    },
  } = useForm({
    onSubmit: postForm as any,
    defaultValues: getNewEmptyDriver({ phoneCode: defaultPhoneCode }),
  });

  const handleClickBack = useCallback(() => {
    onNavigateBack('/todo');
  }, [onNavigateBack]);

  const links = useExternalLinks();

  return (
    <StepLayout hideProgress={true} onClickBack={handleClickBack}>
      <form onSubmit={handleSubmit}>
        <CreateDriverView
          control={control}
          countryCode={countryCode}
          licensingAuthorityTypes={licensingAuthorityTypes}
          linkToLicensingAuthorities={links.licensingAuthorities}
          linkToPrivacyPolicy={links.privacyPolicy}
          isSubmitting={isSubmitting}
        />
      </form>
    </StepLayout>
  );
};

export { CreateDriver };
